import { useEffect, useState } from 'react';
import { useEmployee } from '../context/EmployeeContext';
import { useToast } from '../../toast/ToastContext';
import { phoneChars, todayISO } from '../../icib-admin/utils/format';

const FIELDS = ['first_name', 'last_name', 'dob', 'phone', 'address'];

function formFromMe(me) {
  const f = {};
  for (const k of FIELDS) f[k] = me?.[k] || '';
  return f;
}

/* EE-03 — Update personal details.
   "The system shall allow employees to update their personal details." —
   keeps employee records accurate (BrandonUserStoryAPI.md EE-03, PATCH /me).

   Identity fields only (first_name, last_name, dob, phone, address
   — §2.1, README §4.1). Gender is deliberately hidden from employees and
   left out of the PATCH so it's kept as-is; admins still see/manage it.
   Group-specific `form_data` isn't editable here — there's
   no self-service path for it; only icib_admin can correct it, via
   ICIB-02d — see EE-02's Scheme details tab for the read-only view. Only
   the fields the person actually changed are sent, same pattern as
   ICIB-02b's Update; the identity email field is deliberately not shown
   here — it's the Entra login-resolution key, not a plain profile field
   (§2.8), and PATCH /me doesn't accept it. */
function DetailsView() {
  const { db, status, refreshing, updateMe } = useEmployee();
  const toast = useToast();
  const [form, setForm] = useState(() => formFromMe(db.me));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(formFromMe(db.me)); }, [db.me]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await updateMe(form);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not save your details.', 'bad', res.error);
      return;
    }
    toast('Details updated.', 'ok');
  }

  if (status === 'loading') {
    return <div className="card"><div className="empty"><b>Loading…</b></div></div>;
  }
  if (status === 'error' || !db.me) {
    return (
      <div className="card">
        <div className="empty">
          <b>Couldn't load your details</b>
          Check your connection and try again.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="eyebrow">My details</div>
      <div className="page-h"><h2>My details</h2></div>
      <p className="page-sub">
        Update your personal details. Changes apply to every scheme you're enrolled in.{refreshing ? ' Refreshing…' : ''}
      </p>

      <div className="card card-b">
        <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
          <h4>Your details</h4>
          <span className="spacer" />
          <span className="ep">PATCH /me</span>
        </div>

        <form onSubmit={submit}>
          <div className="grid g2">
            <div className="field">
              <label>First name</label>
              <input value={form.first_name} onChange={set('first_name')} />
            </div>
            <div className="field">
              <label>Last name</label>
              <input value={form.last_name} onChange={set('last_name')} />
            </div>
            <div className="field">
              <label>Date of birth <span className="req">*</span></label>
              <input type="date" required max={todayISO()} value={form.dob} onChange={set('dob')} />
            </div>
            <div className="field">
              <label>Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: phoneChars(e.target.value) }))} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Address</label>
              <input value={form.address} onChange={set('address')} />
            </div>
            <div className="field">
              <label>Email</label>
              <input value={db.me.email || ''} disabled />
              <div className="hint">Not editable here — email changes go through your Entra account.</div>
            </div>
          </div>

          <div className="note" style={{ marginBottom: 14 }}>
            Company-specific details (e.g. cost centre, eligibility answers) aren't editable here —
            those can only be corrected by ICIB admin.
          </div>
          <div className="btn-row">
            <button type="submit" className="btn pri" disabled={busy}>{busy ? 'Saving…' : 'Save details'}</button>
          </div>
        </form>
      </div>
    </>
  );
}

export default DetailsView;
